import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Heading,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Select,
    Radio,
    RadioGroup,
    Checkbox,
    Button,
    Container,
    Stack,
    useToast,
} from "@chakra-ui/react";

const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";
const GATEWAY_API_URL =
    "https://digitalfields.co.za/Gateway/rgeneng/qa/ws1.cfc";

const AddCandidate = () => {
    // Parameters neccessary for making api requests
    const HuserID = localStorage.getItem("HuserID");
    const UUID = localStorage.getItem("UUID");

    // State for managing form data
    const [formData, setFormData] = useState({
        title: "",
        firstName: "",
        surname: "",
        emailAddress: "",
        cellNumber: "",
        idNumber: "",
        empPlacement: "",
        gender: "",
        ethnicity: "",
        disability: false,
        disabilityType: "",
        nationality: "",
        homeLanguage: "",
        qualification: "",
        province: "",
    });

    // Setting errors for form data validation
    const [formErrors, setFormErrors] = useState({
        title: "",
        firstName: "",
        surname: "",
        emailAddress: "",
        cellNumber: "",
        idNumber: "",
        empPlacement: "",
    });

    // User feedback cues
    const toast = useToast();

    // Posting new Transnet candidate data:
    const postNewCandidate = async () => {
        try {
            fetch(
                `${API_BASE_URL}?method=addCandidateTransnet&huserID=${HuserID}&uuid=${UUID}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(formData),
                    mode: "no-cors",
                }
            );
        } catch (error) {
            toast({
                title: "Error",
                description: error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
            });
            throw error;
        } finally {
            toast({
                title: "Candidate Successfully Added!",
                description: "",
                status: "success",
                duration: 6000,
                isClosable: true,
            });
        }
    };

    // Phone validation for South African phone numbers
    const validatePhoneNumber = (phone) => {
        if (!phone) return "Phone number is required";

        const cleaned = phone.trim().replace(/\s+/g, "");
        const phoneRegex = /^(0[6-8][0-9]{8}|\+27[6-8][0-9]{8})$/;

        if (!cleaned) {
            return "Phone number is required";
        } else if (!phoneRegex.test(cleaned)) {
            return "Please enter a valid South African phone number";
        }

        return "";
    };

    // ID validation for 9-digit ID number
    const validateIdNumber = (id) => {
        const cleaned = id.trim();

        if (!cleaned) {
            return "ID number is required";
        }

        // SA ID number: YYMMDD + 7 digits (total 13 digits)
        const idRegex = /^\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{7}$/;

        if (!idRegex.test(cleaned)) {
            return "Please enter a valid 13-digit South African ID number";
        }

        return "";
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;

        // Update form data
        setFormData((prev) => ({
            ...prev,
            [name]: name === "disability" ? checked : value,
        }));

        // Validate the specific field and update formErrors object
        let errorMessage = "";

        if (name === "cellNumber") {
            errorMessage = validatePhoneNumber(value);
        } else if (name === "idNumber") {
            errorMessage = validateIdNumber(value);
        }

        // Only update formErrors if validation applies to the field
        if (name === "cellNumber" || name === "idNumber") {
            setFormErrors((prev) => ({
                ...prev,
                [name]: errorMessage,
            }));
        }
    };

    // Validate Employee Placement (Internal/External) radio input
    const validateRadio = () => {
        let empPlacementError = "";
        let isValid = true;

        if (!formData.empPlacement) {
            empPlacementError = "Please select an option";
            isValid = false;
        }

        setFormErrors({ ...formErrors, empPlacement: empPlacementError });
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all required fields before submission
        if (!validateRadio()) {
            return;
        }
        const phoneError = validatePhoneNumber(formData.cellNumber);
        const idError = validateIdNumber(formData.idNumber);

        setFormErrors({
            cellNumber: phoneError,
            idNumber: idError,
        });

        // If no errors, proceed with form submission
        if (!phoneError && !idError) {
            return await postNewCandidate();
        }
    };

    return (
        <Container
            className="max-w-md mx-auto mt-8 p-6 bg-white rounded shadow"
            p={5}
        >
            <Heading className="text-xl font-bold mb-4" mb={5}>
                Add Candidate - Transnet
            </Heading>
            <form onSubmit={handleSubmit}>
                <FormControl>
                    {/* Title selection input */}
                    <Select
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Candidate Title"
                        isRequired
                        my="5"
                    >
                        <option>Mr</option>
                        <option>Mrs</option>
                        <option>Miss</option>
                        <option>Dr</option>
                    </Select>

                    {/* First Name text input */}
                    <FormLabel>First Name</FormLabel>
                    <Input
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        type="text"
                        isRequired
                    />

                    {/* Surname text input */}
                    <FormLabel>Surname</FormLabel>
                    <Input
                        name="surname"
                        value={formData.surname}
                        onChange={handleChange}
                        type="text"
                        isRequired
                    />

                    {/* Email Address input */}
                    <Input
                        name="emailAddress"
                        value={formData.emailAddress}
                        onChange={handleChange}
                        type="email"
                        isRequired
                        placeholder="Email Address"
                        my="5"
                    />

                    {/* Cellphone number input */}
                    <FormControl isInvalid={!!formErrors.cellNumber} my="5">
                        <Input
                            name="cellNumber"
                            value={formData.cellNumber}
                            onChange={handleChange}
                            type="number"
                            isRequired
                            placeholder="Cellphone Number"
                        />
                        <FormErrorMessage>
                            {formErrors.cellNumber}
                        </FormErrorMessage>
                    </FormControl>

                    {/* ID number input */}
                    <FormControl isInvalid={!!formErrors.idNumber} my="5">
                        <Input
                            name="idNumber"
                            value={formData.idNumber}
                            onChange={handleChange}
                            type="number"
                            isRequired
                            placeholder="ID Number"
                        />
                        <FormErrorMessage>
                            {formErrors.idNumber}
                        </FormErrorMessage>
                    </FormControl>

                    {/* Internal / External Placement option radio button input */}
                    <FormControl isInvalid={!!formErrors.empPlacement}>
                        <FormLabel>Employee Placement:</FormLabel>

                        <RadioGroup>
                            <Stack direction="row">
                                <Radio
                                    name="empPlacement"
                                    value="0"
                                    onChange={handleChange}
                                >
                                    Internal
                                </Radio>
                                <Radio
                                    name="empPlacement"
                                    value="1"
                                    onChange={handleChange}
                                >
                                    External
                                </Radio>
                            </Stack>
                        </RadioGroup>
                        <FormErrorMessage>
                            {formErrors.empPlacement}
                        </FormErrorMessage>
                    </FormControl>

                    {/* Gender selection input */}
                    <Select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        placeholder="Gender"
                        my="5"
                    >
                        <option>Male</option>
                        <option>Female</option>
                        <option>Prefer not to say</option>
                    </Select>

                    {/* Ethnicity selection input */}
                    <Select
                        name="ethnicity"
                        value={formData.ethnicity}
                        onChange={handleChange}
                        placeholder="Ethnicity"
                        my="5"
                    >
                        <option>Asian</option>
                        <option>Black</option>
                        <option>Coloured</option>
                        <option>White</option>
                    </Select>

                    {/* Disability YN checkbox input */}
                    <Checkbox
                        name="disability"
                        isChecked={formData.disability}
                        value={formData.disability}
                        onChange={handleChange}
                    >
                        Disabled
                    </Checkbox>

                    {/* Disability Type text input (Only available if disability checkbox value is set to 1)*/}
                    <Input
                        name="disabilityType"
                        value={formData.disabilityType}
                        onChange={handleChange}
                        type="text"
                        placeholder="Disability Type / Details"
                        my="5"
                    />

                    {/* Nationality selection input */}
                    <Select
                        name="nationality"
                        value={formData.nationality}
                        onChange={handleChange}
                        placeholder="Nationality"
                        my="5"
                    >
                        <option>South African</option>
                        <option>Outside of RSA</option>
                    </Select>

                    {/* Home Language selection input */}
                    <Select
                        name="homeLanguage"
                        value={formData.homeLanguage}
                        onChange={handleChange}
                        placeholder="Home Language"
                        my="5"
                    >
                        <option>isiZulu</option>
                        <option>Afrikaans</option>
                        <option>English</option>
                        <option>isiXhosa</option>
                        <option>Sesotho</option>
                        <option>isiNdebele</option>
                        <option>Setswana</option>
                        <option>siSwati</option>
                        <option>Tshivenda</option>
                        <option>Xitsonga</option>
                        <option>Northern Sotho</option>
                        <option>Other</option>
                    </Select>

                    {/* Highest Qualification selection input */}
                    <Select
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                        placeholder="Highest Qualification"
                        my="5"
                    >
                        <option>
                            Matric/National Senior Certificate (NSC)
                        </option>
                        <option>Diploma</option>
                        <option>Undergraduate</option>
                        <option>Masters</option>
                        <option>PHD</option>
                    </Select>

                    {/* Province of Residence selection input */}
                    <Select
                        name="province"
                        value={formData.province}
                        onChange={handleChange}
                        placeholder="Province of Residence"
                        my="5"
                    >
                        <option>Eastern Cape</option>
                        <option>Free State</option>
                        <option>Gauteng</option>
                        <option>KwaZulu-Natal</option>
                        <option>Limpopo</option>
                        <option>Mpumalanga</option>
                        <option>Northern Cape</option>
                        <option>North West</option>
                        <option>Western Cape</option>
                        <option>Outside RSA</option>
                    </Select>

                    {/* Form submit button */}
                    <Button type="submit" mb="8">
                        Submit
                    </Button>
                </FormControl>
            </form>
        </Container>
    );
};

export default AddCandidate;
